import { useState, useEffect, useRef } from "react";

interface IBarcodeScannerConfig {
  /** Max milliseconds between key presses to count as scanner input */
  interKeyDelay?: number;
  /** Max total duration of the whole scan before Enter/Tab (ms) */
  maxTotalDuration?: number;
  /** Minimum characters for a valid scan */
  minLength?: number;
  /** If true, prevents default behavior on scan completion */
  preventDefault?: boolean;
  /** When false, scanner detection is disabled (e.g. while a modal is open). */
  enabled?: boolean;
}

/**
 * Detects barcode/QR code scans by measuring typing speed.
 * Works globally without requiring focus on any input field.
 */
export default function useBarcodeScanner(
  onScan: (sku: string) => void,
  config: IBarcodeScannerConfig = {},
): { isScannerInput: boolean } {
  const {
    interKeyDelay = 40, // 40ms max between keys for scanner
    maxTotalDuration = 2000, // 2 seconds max for full scan
    minLength = 6, // Minimum 6 characters
    preventDefault = true,
    enabled = true,
  } = config;

  const [isScannerInput, setIsScannerInput] = useState<boolean>(false);
  const inputBufferRef = useRef<string>("");
  const firstKeyTimeRef = useRef<number | null>(null);
  const lastKeyTimeRef = useRef<number>(0);
  const keyTimingsRef = useRef<number[]>([]);
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore special keys entirely
      const ignoredKeys = [
        'Backspace', 'Delete', 'Shift', 'Control', 'Alt', 'Meta',
        'CapsLock', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Home', 'End', 'PageUp', 'PageDown', 'Insert', 'F1', 'F2', 'F3', 'F4',
        'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
      ];

      if (ignoredKeys.includes(event.key)) {
        return;
      }

      const currentTime = Date.now();
      const timeSinceLastKey = currentTime - lastKeyTimeRef.current;

      // Clear any existing auto-reset timeout
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }

      if (event.key === "Enter" || event.key === "Tab") {
        const totalDuration = currentTime - (firstKeyTimeRef.current ?? currentTime);
        const bufferLength = inputBufferRef.current.length;

        // Calculate average timing between keys
        const avgTiming = keyTimingsRef.current.length > 0
          ? keyTimingsRef.current.reduce((a, b) => a + b, 0) / keyTimingsRef.current.length
          : 999;


        const isValidScan =
          bufferLength >= minLength &&
          totalDuration < maxTotalDuration &&
          avgTiming < interKeyDelay &&
          timeSinceLastKey < interKeyDelay;

        console.log('Scan attempt:', {
          buffer: inputBufferRef.current.substring(0, 50) + (bufferLength > 50 ? '...' : ''),
          bufferLength,
          totalDuration,
          avgTiming: avgTiming.toFixed(1) + 'ms',
          lastKeyTiming: timeSinceLastKey + 'ms',
          isValidScan,
        });

        if (isValidScan) {
          console.log('SCANNER DETECTED - Adding to the cart');

          // Prevent the Enter/Tab from affecting any focused input
          if (preventDefault) {
            event.preventDefault();
            event.stopPropagation();
          }

          // Trigger the scan callback
          onScan(inputBufferRef.current);
        } else {
          console.log('⌨️ KEYBOARD INPUT - Normal behavior');
        }

        // Reset everything
        inputBufferRef.current = "";
        firstKeyTimeRef.current = null;
        lastKeyTimeRef.current = 0;
        keyTimingsRef.current = [];
        setIsScannerInput(false);
        return;
      }

      // Handle regular character input
      if (event.key?.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        // If there was a long pause, treat this as a fresh start
        if (lastKeyTimeRef.current > 0 && timeSinceLastKey > 1000) {
          console.log('Long pause detected, resetting for fresh scan');
          inputBufferRef.current = "";
          firstKeyTimeRef.current = null;
          keyTimingsRef.current = [];
        }

        // First character
        if (!firstKeyTimeRef.current) {
          firstKeyTimeRef.current = currentTime;
          console.log('Started input sequence');
        } else {
          // Record timing for averaging
          keyTimingsRef.current.push(timeSinceLastKey);
        }

        // Update scanner detection based on current timing
        const isFastTyping = lastKeyTimeRef.current > 0 && timeSinceLastKey < interKeyDelay;
        setIsScannerInput(isFastTyping);

        // Build buffer
        inputBufferRef.current += event.key;
        lastKeyTimeRef.current = currentTime;

        // Auto-reset if typing stops for too long
        resetTimeoutRef.current = setTimeout(() => {
          console.log('⏱️ Input timeout - resetting');
          inputBufferRef.current = "";
          firstKeyTimeRef.current = null;
          lastKeyTimeRef.current = 0;
          keyTimingsRef.current = [];
          setIsScannerInput(false);
        }, interKeyDelay + 200);
      }
    };

    // Listen globally on the window
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, [onScan, interKeyDelay, maxTotalDuration, minLength, preventDefault, enabled]);

  return { isScannerInput };
}